import styled, { keyframes, css, } from "styled-components";


export declare type FieldLoaderProps = {
  style?: React.CSSProperties;
	type?: "ellipsis";
	scheme?: "light" | "dark";
	color?: string;
	backgroundColor?: string;
	center?: boolean;
}



/**
 * A component that displays a loading indicator for fields or content areas
 * 
 * @remarks
 * This component renders an animated ellipsis loading indicator.
 * It can be customized with different colors and positioning options.
 * 
 * @param {FieldLoaderProps} props - Component props
 * @param {string} [props.type="ellipsis"] - The type of loading animation
 * @param {React.CSSProperties} [props.style={}] - Additional CSS styles
 * @param {"light" | "dark"} [props.scheme] - Color scheme for the loader
 * @param {string} [props.color] - Custom color for the loader dots
 * @param {string} [props.backgroundColor] - Background color for the loader container
 * @param {boolean} [props.center=false] - Whether to center the loader horizontally
 * @returns {JSX.Element} The rendered field loader component
 */
export const FieldLoader: React.FC<FieldLoaderProps> = ({
	type = "ellipsis",
	style = {},
	scheme,
	color,
	backgroundColor,
	center = false,
}) => {
	return (
		<LoaderContainer
			style={style}
			$color={color}
			$backgroundColor={backgroundColor}
			$scheme={scheme}
			$center={center}
		>
			<Brace $center={center}>
				{[1, 2, 3, 4].map((i) => (
					<Dot key={i} $index={i} />
				))}
			</Brace>
		</LoaderContainer>
	);
};



const LoaderContainer = styled.div<{
	$color?: string;
	$scheme?: "light" | "dark";
	$backgroundColor?: string;
	$center?: boolean;
}>`
	display: flex;
	align-items: center;
	width: 100%;
	height: 0px;
	color: ${({ $color, $scheme }) =>
		$color ||
		($scheme === "light"
			? "white"
			: $scheme === "dark"
			? "black"
			: "currentColor")};
	background-color: ${({ $backgroundColor }) => $backgroundColor || "transparent"};
`;

const BusyText = styled.p`
	color: white;
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 150px;
`;


const Brace = styled.div<{ $center?: boolean; $scale?: number; }>`
	position: relative;
	min-width: 80px;
  min-height: 15px;
	margin: ${({ $center }) => ($center ? "auto" : "0")};
	scale: ${({ $scale }) => $scale || 0.5};
`;


const Dot = styled.div<{ $index: number; }>`
	box-sizing: border-box;
	position: absolute;
	width: 13.33333px;
	height: 13.33333px;
	border-radius: 50%;
	background: currentColor;
	animation-timing-function: cubic-bezier(0, 1, 1, 0);

	${({ $index }) => {
		if ($index === 1)
			return css`
				left: 8px;
				animation: ${ldsEllipsis1} 0.6s infinite;
			`;
		if ($index === 2)
			return css`
				left: 8px;
				animation: ${ldsEllipsis2} 0.6s infinite;
			`;
		if ($index === 3)
			return css`
				left: 32px;
				animation: ${ldsEllipsis2} 0.6s infinite;
			`;
		if ($index === 4)
			return css`
				left: 56px;
				animation: ${ldsEllipsis3} 0.6s infinite;
			`;
	}}
`;

const ldsEllipsis1 = keyframes`
  0% { transform: scale(0); }
  100% { transform: scale(1); }
`;

const ldsEllipsis2 = keyframes`
  0% { transform: translate(0, 0); }
  100% { transform: translate(24px, 0); }
`;

const ldsEllipsis3 = keyframes`
  0% { transform: scale(1); } 
  100% { transform: scale(0); }
`;

